import type { CalcDependency, CalcScope, ParameterDef, SetDef, VisualSpec } from '../visualSpec'
import { cleanBracketedFieldRef, normalizeTableauDatasourceKey, normalizeTableauFieldKey } from '../../tableau/fieldIdentity'

export interface ResolveResult {
  sql: string
  dependencies: CalcDependency[]
  errors: string[]
}

function normalizeKey(s: string): string {
  return String(s || '').trim().toLowerCase()
}

function keyVariants(s: string): string[] {
  const base = normalizeKey(s)
  if (!base) return []
  const out = new Set<string>()
  out.add(base)
  // Tableau internal tokens sometimes appear with leading ':' or wrapper prefixes/suffixes.
  // We use the shared field identity normalizer for robust matching.
  const normFieldKey = normalizeTableauFieldKey(base)
  if (normFieldKey) out.add(normFieldKey)
  return Array.from(out.values()).filter(Boolean)
}

function compactKey(s: string): string {
  return normalizeKey(s).replace(/[^a-z0-9]+/g, '')
}

function relationLocalFieldVariantEntries(s: string): Array<{ key: string; score: number }> {
  const raw = String(s ?? '').trim()
  if (!raw) return []
  const out = new Map<string, number>()
  const addKey = (key: string, score: number) => {
    const k = String(key ?? '').trim()
    if (!k) return
    const existing = out.get(k) ?? 0
    if (score > existing) out.set(k, score)
  }
  const add = (v: string, score: number) => {
    const text = String(v ?? '').trim()
    if (!text) return
    addKey(normalizeKey(text), score)
    addKey(normalizeTableauFieldKey(text), score)
    const compact = compactKey(text)
    if (compact.length >= 3) addKey(compact, Math.max(1, score - 10))
  }
  add(raw, 20)
  const cleaned = cleanBracketedFieldRef(raw) || raw
  add(cleaned, 30)
  const withoutParens = cleaned.replace(/\s*\([^)]*\)\s*$/g, '').trim()
  add(withoutParens, 50)
  add(withoutParens.replace(/_/g, ' '), 55)
  add(cleaned.replace(/_/g, ' '), 35)
  return Array.from(out.entries())
    .filter(([key]) => Boolean(key))
    .map(([key, score]) => ({ key, score }))
}

function relationLocalFieldVariants(s: string): string[] {
  return relationLocalFieldVariantEntries(s).map((entry) => entry.key)
}

function relationLocalIdentityKey(s: string): string {
  const raw = String(s ?? '').trim()
  if (!raw) return ''
  const cleaned = cleanBracketedFieldRef(raw) || raw
  const withoutParens = cleaned.replace(/\s*\([^)]*\)\s*$/g, '').trim()
  const readable = withoutParens.replace(/_/g, ' ')
  return normalizeKey(readable) || normalizeTableauFieldKey(withoutParens) || compactKey(withoutParens)
}

function relationLocalFieldTieBreakScore(fieldId: string): number {
  const id = String(fieldId ?? '').trim()
  if (!id) return 0
  let score = 0
  if (/^\[[^\]]+\]$/.test(id)) score += 4
  if (!id.includes(':')) score += 2
  if (/^\[(?:none|usr|sum|avg|min|max|cnt|ctd):/i.test(id)) score -= 2
  return score
}

function matchesParamName(param: ParameterDef | null | undefined, rawFieldName: string): boolean {
  const want = new Set<string>([...keyVariants(rawFieldName), normalizeKey(rawFieldName)].filter(Boolean))
  if (want.size === 0) return false
  const candidates = [
    String((param as any)?.name ?? ''),
    String((param as any)?.caption ?? ''),
    String((param as any)?.raw_name ?? ''),
    String((param as any)?.label ?? ''),
  ].map((x) => x.trim()).filter(Boolean)
  for (const candidate of candidates) {
    const variants = new Set<string>([...keyVariants(candidate), normalizeKey(candidate)].filter(Boolean))
    for (const variant of variants) {
      if (want.has(variant)) return true
    }
  }
  return false
}

function quoteIdent(id: string): string {
  const s = String(id ?? '')
  return `"${s.replaceAll('"', '""')}"`
}

function quoteStringLiteral(v: string): string {
  return `'${String(v ?? '').replaceAll("'", "''")}'`
}

function tryCoerceTimestampLikeToSql(v: any): string | null {
  if (v === null || v === undefined) return null

  // Native JS Date
  if (v instanceof Date) {
    const iso = v.toISOString()
    return `TRY_CAST(${quoteStringLiteral(iso)} AS TIMESTAMP)`
  }

  // Best-effort: objects with toISOString (Date-like)
  try {
    const fn = (v as any)?.toISOString
    if (typeof fn === 'function') {
      const iso = String(fn.call(v) ?? '').trim()
      if (iso) return `TRY_CAST(${quoteStringLiteral(iso)} AS TIMESTAMP)`
    }
  } catch { }

  // Apache Arrow often stringifies timestamps like: Timestamp(Microsecond, 1770076800000000)
  // Normalize that into TO_TIMESTAMP(seconds_since_epoch).
  try {
    const s = String(v ?? '').trim()
    if (!s) return null
    // Tableau date literals can appear as #YYYY-MM-DD# (or with time).
    const unwrapped = (s.startsWith('#') && s.endsWith('#') && s.length >= 2)
      ? s.slice(1, -1).trim()
      : s
    // ISO-ish date/time strings should be treated as temporal values, not VARCHAR constants.
    if (/^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}:\d{2}(?:\.\d+)?)?$/.test(unwrapped)) {
      return `TRY_CAST(${quoteStringLiteral(unwrapped)} AS TIMESTAMP)`
    }
    // Epoch milliseconds surfaced as a string.
    if (/^-?\d+$/.test(unwrapped)) {
      const n = Number(unwrapped)
      if (Number.isFinite(n) && Math.abs(n) >= 1e12) {
        return `(CAST(TO_TIMESTAMP((CAST(${unwrapped} AS DOUBLE) / 1000.0)) AS TIMESTAMP))`
      }
    }
    const m = s.match(/^Timestamp\(\s*([A-Za-z]+)\s*,\s*(-?\d+)\s*\)$/)
    if (m) {
      const unit = String(m[1] ?? '').trim().toLowerCase()
      const n = String(m[2] ?? '').trim()
      const denom =
        (unit === 'second' || unit === 'seconds') ? 1
          : (unit === 'millisecond' || unit === 'milliseconds') ? 1_000
            : (unit === 'microsecond' || unit === 'microseconds') ? 1_000_000
              : (unit === 'nanosecond' || unit === 'nanoseconds') ? 1_000_000_000
                : null
      if (denom) {
        // DuckDB returns TIMESTAMP WITH TIME ZONE from TO_TIMESTAMP(); cast to plain TIMESTAMP
        // so downstream interval arithmetic works (`TIMESTAMP + INTERVAL`).
        return `(CAST(TO_TIMESTAMP((CAST(${n} AS DOUBLE) / ${String(denom)}.0)) AS TIMESTAMP))`
      }
    }
  } catch { }

  // Last-resort: numeric valueOf (some wrappers implement this)
  try {
    const vv = (v as any)?.valueOf?.()
    if (typeof vv === 'number' && Number.isFinite(vv) && Math.abs(vv) >= 1e12) {
      return `(CAST(TO_TIMESTAMP((CAST(${String(vv)} AS DOUBLE) / 1000.0)) AS TIMESTAMP))`
    }
  } catch { }

  return null
}

export interface ResolveContext {
  spec: VisualSpec
  scope: CalcScope
}

export function resolveFieldOrParamOrSet(
  ctx: ResolveContext,
  ref: { source?: string | null; field: string },
): ResolveResult {
  const errors: string[] = []
  const dependencies: CalcDependency[] = []

  const fieldName = String(ref.field || '').trim()
  const key = normalizeKey(fieldName)
  if (!key) return { sql: 'NULL', dependencies, errors: ['Empty field reference'] }

  const params = (ctx.spec.data_pane.parameters || []) as ParameterDef[]
  const sets = (ctx.spec.data_pane.sets || []) as SetDef[]

  const param = params.find((p) => matchesParamName(p, fieldName)) || null
  const srcKey = normalizeKey(String(ref.source || ''))
  const isParamQualified = srcKey === 'parameters' || srcKey === 'parameter'
  if ((!ref.source || isParamQualified) && param) {
    dependencies.push({ kind: 'param', id: param.id })
    const templateSql = String((param as any)?.template_sql ?? '').trim()
    if (templateSql) {
      return { sql: templateSql, dependencies, errors }
    }
    const v = param.current_value
    const dt = normalizeKey(String((param as any)?.data_type ?? 'unknown'))
    const isDate = dt === 'date'
    const isTimestamp =
      dt === 'timestamp' ||
      dt === 'datetime' ||
      dt === 'date_time' ||
      dt === 'timestamptz' ||
      dt === 'timestamp_tz'
    if (v === null || v === undefined) {
      if (isDate) return { sql: 'CAST(NULL AS DATE)', dependencies, errors }
      if (isTimestamp) return { sql: 'CAST(NULL AS TIMESTAMP)', dependencies, errors }
      if (dt === 'number' || dt === 'numeric' || dt === 'integer' || dt === 'real' || dt === 'double' || dt === 'float' || dt === 'decimal') {
        return { sql: 'CAST(NULL AS DOUBLE)', dependencies, errors }
      }
      if (dt === 'bool' || dt === 'boolean') return { sql: 'CAST(NULL AS BOOLEAN)', dependencies, errors }
      if (dt === 'string' || dt === 'text') return { sql: 'CAST(NULL AS VARCHAR)', dependencies, errors }
      // Best-effort: many workbook parameter names embed type hints.
      const nameHint = String((param as any)?.name ?? '').toLowerCase()
      if (nameHint.includes('date') || nameHint.includes('time')) {
        return { sql: 'CAST(NULL AS TIMESTAMP)', dependencies, errors }
      }
      return { sql: 'NULL', dependencies, errors }
    }
    if (typeof v === 'number') {
      const looksEpochMs = Number.isFinite(v) && Math.abs(v) >= 1e12
      if (isDate || isTimestamp || looksEpochMs) {
        // Our pipelines often represent Tableau dates/timestamps as epoch milliseconds (BIGINT).
        // Render a proper TIMESTAMP expression so date arithmetic like `+ INTERVAL 1 DAY` works.
        // Important: avoid `(<bigint> * INTERVAL 1 MILLISECOND)` because DuckDB can internally
        // downcast the multiplier to INT32 and overflow for modern epoch-ms values.
        // `to_timestamp()` expects seconds since epoch.
        const ts = `(CAST(TO_TIMESTAMP((CAST(${String(v)} AS DOUBLE) / 1000.0)) AS TIMESTAMP))`
        return { sql: isDate ? `CAST(${ts} AS DATE)` : ts, dependencies, errors }
      }
      return { sql: String(v), dependencies, errors }
    }
    if (typeof v === 'boolean') return { sql: v ? 'TRUE' : 'FALSE', dependencies, errors }

    // Date/timestamp parameters can surface as objects (e.g. Arrow Timestamp), and are often
    // untyped in parity/dev pipelines (data_type="unknown"). Coerce whenever we detect a
    // timestamp-like shape to avoid generating `'Timestamp(...)'` string literals.
    const tsSql = tryCoerceTimestampLikeToSql(v)
    if (tsSql) return { sql: isDate ? `CAST(${tsSql} AS DATE)` : tsSql, dependencies, errors }

    if (typeof v === 'string') {
      // Untyped parameters are common in parity/dev payloads. If the parameter name is clearly
      // temporal, compile as TIMESTAMP so CASE/DATEADD branches stay type-consistent.
      const nameHint = String((param as any)?.name ?? '').toLowerCase()
      if (nameHint.includes('date') || nameHint.includes('time')) {
        return { sql: `TRY_CAST(${quoteStringLiteral(String(v).trim())} AS TIMESTAMP)`, dependencies, errors }
      }
    }
    return { sql: quoteStringLiteral(String(v)), dependencies, errors }
  }

  const set = (() => {
    const want = new Set<string>([...keyVariants(fieldName), key].filter(Boolean))
    const srcKey = normalizeTableauDatasourceKey(String(ref.source || ''))
    for (const s of sets) {
      const nm = String(s?.name ?? s?.raw_name ?? '').trim()
      if (!nm) continue
      const nmKeys = new Set<string>([...keyVariants(nm), normalizeKey(nm)].filter(Boolean))
      const nameMatch = Array.from(nmKeys).some((k) => want.has(k))
      if (!nameMatch) continue
      if (!srcKey) return s
      const ds = String((s as any)?.datasource ?? '').trim()
      if (!ds) return s
      const dsKey = normalizeTableauDatasourceKey(ds)
      if (dsKey && dsKey === srcKey) return s
    }
    return null
  })()
  if (set) {
    dependencies.push({ kind: 'set', id: set.id })
    // Set reference alone isn't valid SQL; caller should interpret as membership list.
    return { sql: `__SET__(${quoteStringLiteral(set.name)})`, dependencies, errors }
  }

  const sources = (ctx.spec.data_pane.sources || []) as any[]

  const matchFieldInSource = (src: any, f: string): { sourceName: string; fieldId: string } | null => {
    const fields = Array.isArray(src?.fields) ? src.fields : []
    const needleRaw = normalizeKey(f)
    const needleKey = normalizeTableauFieldKey(f)
    const needle = new Set<string>([...keyVariants(f), needleRaw, needleKey].filter(Boolean))
    for (const df of fields) {
      const idRaw = String(df?.id ?? '')
      const labelRaw = String(df?.label ?? '')

      // First pass: exact normalized match (fast + deterministic).
      const id = normalizeKey(idRaw)
      const label = normalizeKey(labelRaw)
      if (id && needle.has(id)) return { sourceName: String(src?.name ?? src?.id ?? ''), fieldId: String(df.id) }
      if (label && needle.has(label)) return { sourceName: String(src?.name ?? src?.id ?? ''), fieldId: String(df.id) }

      // Second pass: variant match (Tableau internal token normalization).
      const idVars = keyVariants(idRaw)
      for (const v of idVars) {
        if (needle.has(v)) return { sourceName: String(src?.name ?? src?.id ?? ''), fieldId: String(df.id) }
      }
      const labelVars = keyVariants(labelRaw)
      for (const v of labelVars) {
        if (needle.has(v)) return { sourceName: String(src?.name ?? src?.id ?? ''), fieldId: String(df.id) }
      }

      // Third pass: normalized Tableau field keys
      const idKey = normalizeTableauFieldKey(idRaw)
      const labelKey = normalizeTableauFieldKey(labelRaw)
      if (idKey && needle.has(idKey)) return { sourceName: String(src?.name ?? src?.id ?? ''), fieldId: String(df.id) }
      if (labelKey && needle.has(labelKey)) return { sourceName: String(src?.name ?? src?.id ?? ''), fieldId: String(df.id) }
    }
    const looseNeedle = new Set<string>(relationLocalFieldVariants(f))
    if (looseNeedle.size) {
      const looseHits = new Map<string, {
        hit: { sourceName: string; fieldId: string }
        score: number
        identity: string
        tieBreakScore: number
        order: number
      }>()
      for (let order = 0; order < fields.length; order++) {
        const df = fields[order]
        const fieldId = String(df?.id ?? '')
        if (!fieldId) continue
        const fieldEntries = [
          ...relationLocalFieldVariantEntries(fieldId),
          ...relationLocalFieldVariantEntries(String(df?.label ?? '')),
        ]
        const score = fieldEntries
          .filter((entry) => looseNeedle.has(entry.key))
          .reduce((max, entry) => Math.max(max, entry.score), 0)
        if (score <= 0) continue
        const existing = looseHits.get(fieldId)
        if (!existing || score > existing.score) {
          looseHits.set(fieldId, {
            hit: { sourceName: String(src?.name ?? src?.id ?? ''), fieldId },
            score,
            identity: relationLocalIdentityKey(fieldId),
            tieBreakScore: relationLocalFieldTieBreakScore(fieldId),
            order,
          })
        }
      }
      const ranked = Array.from(looseHits.values()).sort((a, b) =>
        b.score - a.score || b.tieBreakScore - a.tieBreakScore || a.order - b.order,
      )
      if (ranked.length === 1) return ranked[0].hit
      if (ranked.length > 1 && ranked[0].score > ranked[1].score) return ranked[0].hit
      if (ranked.length > 1) {
        const topScore = ranked[0].score
        const tied = ranked.filter((entry) => entry.score === topScore)
        const identities = new Set(tied.map((entry) => entry.identity).filter(Boolean))
        if (identities.size === 1) return ranked[0].hit
      }
    }
    return null
  }

  const resolveQualified = (): { sourceName: string; fieldId: string; sourceId?: string } | null => {
    const srcNameKey = normalizeTableauDatasourceKey(String(ref.source || ''))
    if (!srcNameKey) return null
    const suffix = (s: string) => {
      const parts = String(s || '').split('.').map(p => p.trim()).filter(Boolean)
      return parts.length ? parts[parts.length - 1] : String(s || '')
    }
    const candidates = sources.filter((s) => {
      const aliases = [
        ...((Array.isArray((s as any)?.aliases) ? (s as any).aliases : []) as any[]),
        ...((Array.isArray((s as any)?.source_aliases) ? (s as any).source_aliases : []) as any[]),
      ]
      const vars = new Set<string>([
        normalizeTableauDatasourceKey(String(s?.name ?? '')),
        normalizeTableauDatasourceKey(String(s?.id ?? '')),
        normalizeTableauDatasourceKey(String((s as any)?.reference ?? '')),
        normalizeTableauDatasourceKey(suffix(String((s as any)?.reference ?? ''))),
        ...keyVariants(String(s?.name ?? '')),
        ...keyVariants(String(s?.id ?? '')),
        ...keyVariants(String((s as any)?.reference ?? '')),
        ...keyVariants(suffix(String((s as any)?.reference ?? ''))),
        ...aliases.flatMap((alias) => [
          normalizeTableauDatasourceKey(String(alias ?? '')),
          ...keyVariants(String(alias ?? '')),
          ...keyVariants(suffix(String(alias ?? ''))),
        ]),
      ].filter(Boolean))
      return vars.has(srcNameKey)
    })
    const pickFrom = (src: any): { sourceName: string; fieldId: string; sourceId?: string } | null => {
      const hit = matchFieldInSource(src, fieldName)
      if (!hit) return null
      return { ...hit, sourceId: String(src?.id ?? '') }
    }
    if (candidates.length === 0) {
      // Best-effort fallback: if there's only one attached source, treat it as the match.
      if (sources.length === 1) {
        const fallbackHit = pickFrom(sources[0])
        if (fallbackHit) return fallbackHit
      }
      errors.push(`Unknown source: ${String(ref.source)} (attach the datasource first, or qualify with the attached source name)`)
      return null
    }
    if (candidates.length > 1) {
      errors.push(`Ambiguous source name: ${String(ref.source)}`)
      return null
    }
    const hit = pickFrom(candidates[0])
    if (!hit) errors.push(`Unknown field "${fieldName}" in source "${String(ref.source)}"`)
    return hit
  }

  const resolveUnqualified = (): { sourceName: string; fieldId: string; sourceId: string } | null => {
    if (ctx.scope.kind === 'source') {
      const scopeSourceId = (ctx.scope.kind === 'source') ? String((ctx.scope as any).source_id || '') : ''
      const src = sources.find((s) => String(s?.id ?? '').trim() === String(scopeSourceId || '').trim())
      if (!src) {
        errors.push(`Invalid scope source_id: ${String(scopeSourceId)}`)
        return null
      }
      const hit = matchFieldInSource(src, fieldName)
      if (!hit) errors.push(`Unknown field "${fieldName}" in scoped source`)
      return hit ? { ...hit, sourceId: String(src.id) } : null
    }

    // Global: find unique source that contains this field
    const hits: Array<{ sourceName: string; fieldId: string; sourceId: string }> = []
    for (const src of sources) {
      const hit = matchFieldInSource(src, fieldName)
      if (hit) hits.push({ ...hit, sourceId: String(src?.id ?? '') })
    }
    if (hits.length === 0) {
      errors.push(`Unknown field: ${fieldName}`)
      return null
    }
    if (hits.length > 1) {
      errors.push(`Ambiguous field "${fieldName}" found in multiple sources. Qualify as [Source].[Field].`)
      return null
    }
    return hits[0]
  }

  const resolved = ref.source ? resolveQualified() : resolveUnqualified()
  if (!resolved) {
    return { sql: 'NULL', dependencies, errors }
  }

  // Record dependency for lineage.
  dependencies.push({ kind: 'field', id: resolved.fieldId, source_id: (resolved as any).sourceId })

  // Use source name as the SQL alias (consistent with transformer today).
  return {
    sql: `${quoteIdent(resolved.sourceName)}.${quoteIdent(resolved.fieldId)}`,
    dependencies,
    errors,
  }
}
